import Cookies from 'js-cookie';

// Cookie consent interface
export interface ConsentPreferences {
  analytics: boolean;
  performance: boolean;
  functional: boolean;
  marketing: boolean;
}

// Default consent preferences
// Analytics defaults to true (opt-out) so anonymous, IP-anonymized GA4 data
// flows on first visit. The cookie banner still lets users opt out. This is
// POPIA-compliant for anonymized analytics.
const DEFAULT_CONSENT: ConsentPreferences = {
  analytics: true,
  performance: false,
  functional: true,  // Required for basic functionality
  marketing: false,
};

// Cookie configuration
const COOKIE_OPTIONS = {
  expires: 365, // 1 year
  secure: true,
  sameSite: 'strict' as const,
};

const CONSENT_COOKIE_NAME = 'sa-tax-calc-consent';
const CONSENT_VERSION = '1.0';

// Privacy consent management
export class PrivacyConsentManager {
  private preferences: ConsentPreferences = { ...DEFAULT_CONSENT };
  private listeners: ((preferences: ConsentPreferences) => void)[] = [];

  constructor() {
    this.loadPreferences();
  }

  // Load consent preferences from cookies
  private loadPreferences() {
    try {
      const cookieValue = Cookies.get(CONSENT_COOKIE_NAME);
      if (cookieValue) {
        const stored = JSON.parse(cookieValue);
        if (stored.version === CONSENT_VERSION) {
          this.preferences = { ...DEFAULT_CONSENT, ...stored.preferences };
        }
      }
    } catch (error) {
      console.warn('Failed to load consent preferences:', error);
      this.preferences = { ...DEFAULT_CONSENT };
    }
  }

  // Save consent preferences to cookies
  private savePreferences() {
    try {
      const consentData = {
        version: CONSENT_VERSION,
        preferences: this.preferences,
        timestamp: new Date().toISOString(),
      };
      
      Cookies.set(CONSENT_COOKIE_NAME, JSON.stringify(consentData), COOKIE_OPTIONS);

      // The canonical GA4 service subscribes to consent changes (see
      // src/services/analyticsService.ts); nothing to call here directly.

      // Notify listeners
      this.listeners.forEach(listener => listener(this.preferences));
    } catch (error) {
      console.error('Failed to save consent preferences:', error);
    }
  }

  // Get current consent preferences
  getPreferences(): ConsentPreferences {
    return { ...this.preferences };
  }

  // Update specific consent preference
  updatePreference(type: keyof ConsentPreferences, value: boolean) {
    this.preferences[type] = value;
    this.savePreferences();
  }

  // Update all consent preferences
  updatePreferences(preferences: Partial<ConsentPreferences>) {
    this.preferences = { ...this.preferences, ...preferences };
    this.savePreferences();
  }

  // Accept all cookies
  acceptAll() {
    this.preferences = {
      analytics: true,
      performance: true,
      functional: true,
      marketing: true,
    };
    this.savePreferences();
  }

  // Accept only essential cookies
  acceptEssentialOnly() {
    this.preferences = {
      analytics: false,
      performance: false,
      functional: true,
      marketing: false,
    };
    this.savePreferences();
  }

  // Reject all non-essential cookies
  rejectAll() {
    this.acceptEssentialOnly();
  }

  // Check if user has made consent choice
  hasConsented(): boolean {
    return Cookies.get(CONSENT_COOKIE_NAME) !== undefined;
  }

  // Check if specific consent type is granted
  isConsentGranted(type: keyof ConsentPreferences): boolean {
    return this.preferences[type];
  }

  // Subscribe to consent changes
  subscribe(listener: (preferences: ConsentPreferences) => void) {
    this.listeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  // Clear all consent data
  clearConsent() {
    Cookies.remove(CONSENT_COOKIE_NAME);
    this.preferences = { ...DEFAULT_CONSENT };

    // Notify listeners (the GA4 service reacts via its subscription).
    this.listeners.forEach(listener => listener(this.preferences));
  }

  // Get consent summary for privacy policy display
  getConsentSummary() {
    return {
      hasConsented: this.hasConsented(),
      consentDate: this.getConsentDate(),
      preferences: this.getPreferences(),
      version: CONSENT_VERSION,
    };
  }

  // Get the date when consent was given
  private getConsentDate(): string | null {
    try {
      const cookieValue = Cookies.get(CONSENT_COOKIE_NAME);
      if (cookieValue) {
        const stored = JSON.parse(cookieValue);
        return stored.timestamp || null;
      }
    } catch (error) {
      console.warn('Failed to get consent date:', error);
    }
    return null;
  }
}

// Create singleton instance
export const privacyConsent = new PrivacyConsentManager();

// Convenience functions
export const hasUserConsented = () => privacyConsent.hasConsented();
export const isAnalyticsEnabled = () => privacyConsent.isConsentGranted('analytics');
export const isPerformanceTrackingEnabled = () => privacyConsent.isConsentGranted('performance');
export const acceptAllCookies = () => privacyConsent.acceptAll();
export const acceptEssentialOnly = () => privacyConsent.acceptEssentialOnly();
export const rejectAllCookies = () => privacyConsent.rejectAll();
export const updateConsentPreference = (type: keyof ConsentPreferences, value: boolean) => 
  privacyConsent.updatePreference(type, value);

// React hook for consent preferences
export const useConsentPreferences = () => {
  const [preferences, setPreferences] = React.useState(privacyConsent.getPreferences());

  React.useEffect(() => {
    const unsubscribe = privacyConsent.subscribe(setPreferences);
    return unsubscribe;
  }, []);

  return {
    preferences,
    hasConsented: privacyConsent.hasConsented(),
    updatePreference: privacyConsent.updatePreference.bind(privacyConsent),
    updatePreferences: privacyConsent.updatePreferences.bind(privacyConsent),
    acceptAll: privacyConsent.acceptAll.bind(privacyConsent),
    rejectAll: privacyConsent.rejectAll.bind(privacyConsent),
    clearConsent: privacyConsent.clearConsent.bind(privacyConsent),
  };
};

// Privacy policy information
export const PRIVACY_INFO = {
  dataCollected: [
    'Page views and navigation patterns',
    'Calculator usage and feature interactions',
    'Performance metrics and error data',
    'Browser information and device type',
    'Anonymous user identifier (session-based)',
  ],
  dataNotCollected: [
    'Personal income amounts or financial details',
    'Names, email addresses, or contact information',
    'IP addresses (anonymized in analytics)',
    'Precise location data',
    'Personal identification information',
  ],
  dataUsage: [
    'Improve calculator functionality and user experience',
    'Monitor application performance and reliability',
    'Understand feature usage and user preferences',
    'Debug technical issues and errors',
    'Generate anonymous usage statistics',
  ],
  dataRetention: '14 months for analytics data, 90 days for error logs',
  dataSharing: 'Data is only shared with Google Analytics and Sentry for processing. No data is sold or shared with third parties for marketing purposes.',
  userRights: [
    'Opt out of analytics tracking at any time',
    'Clear all stored consent preferences',
    'Request information about data collected',
    'Contact us for privacy-related questions',
  ],
};

// Add React import for the hook
import React from 'react';